import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

/**
 * Gets a cookie value by name
 */
export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
}

/**
 * Gets authorization headers based on uid cookie
 */
export function getAuthHeaders(additionalHeaders = {}) {
  const headers = { ...additionalHeaders };
  const uid = getCookie('UserId');
  if (uid) {
    const encodedUid = btoa(uid);
    headers['Authorization'] = `Basic ${encodedUid}`;
  }
  return headers;
}

/**
 * Extracts error message from a failed API response
 * @param {Response} response - The failed fetch response (will be cloned)
 * @returns {Promise<string>} The error message
 */
async function extractErrorMessage(response) {
  let errorMessage = `API error: ${response.status}`;
  try {
    // Clone the response so we can read it without consuming the original
    const clonedResponse = response.clone();
    const errorData = await clonedResponse.text();
    if (errorData) {
      // Try to parse as JSON first
      try {
        const jsonError = JSON.parse(errorData);
        errorMessage = jsonError.message || jsonError.error || jsonError.title || jsonError;
      } catch {
        // If not JSON, use the text as is
        errorMessage = errorData;
      }
    }
  } catch {
    // Fallback to generic message
  }
  return errorMessage;
}

/**
 * Wrapper around fetch that automatically adds authorization headers
 * Automatically shows error modal for 500 server errors
 * Automatically shows/hides global loading spinner
 */
export async function authorizedFetch(url, options = {}) {
  const { headers = {}, showLoader = true, ...restOptions } = options;
  const authHeaders = getAuthHeaders(headers);
  
  // Show global loader
  if (showLoader) {
    const { showLoading } = await import('../components/ui/GlobalLoader');
    showLoading();
  }
  
  try {
    const response = await fetch(url, {
      ...restOptions,
      headers: authHeaders,
    });
    
    // Check for 500 errors and show modal
    if (!response.ok && (response.status === 500 || response.status === 400)) {
      const errorMessage = await extractErrorMessage(response);
      // Dynamically import to avoid circular dependencies
      const { showServerError } = await import('../components/ui/ErrorModal');
      showServerError(errorMessage);
    }
    
    return response;
  } finally {
    // Hide global loader
    if (showLoader) {
      const { hideLoading } = await import('../components/ui/GlobalLoader');
      hideLoading();
    }
  }
} 